import { motion } from "framer-motion";

const WEEKS = 8;
const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};

export const WeeklyChart = ({ inquiries = [], deckRequests = [] }) => {
  const thisWeek = startOfWeek(new Date());
  const weeks = Array.from({ length: WEEKS }, (_, i) => {
    const start = new Date(thisWeek);
    start.setDate(start.getDate() - (WEEKS - 1 - i) * 7);
    return { start, inquiries: 0, decks: 0 };
  });
  const bucket = (items, key) =>
    items.forEach((it) => {
      const s = startOfWeek(new Date(it.created_at)).getTime();
      const w = weeks.find((x) => x.start.getTime() === s);
      if (w) w[key] += 1;
    });
  bucket(inquiries, "inquiries");
  bucket(deckRequests, "decks");
  const max = Math.max(1, ...weeks.map((w) => w.inquiries + w.decks));
  const total = weeks.reduce((n, w) => n + w.inquiries + w.decks, 0);
  const prev = weeks[WEEKS - 2].inquiries + weeks[WEEKS - 2].decks;
  const cur = weeks[WEEKS - 1].inquiries + weeks[WEEKS - 1].decks;
  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return (
    <div className="border border-gold/15 bg-ink p-6 lg:p-8 mb-12" data-testid="weekly-chart">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow">Last {WEEKS} weeks</div>
          <h2 className="mt-2 font-serif text-2xl text-ivory">
            {total} lead{total === 1 ? "" : "s"} ·{" "}
            <span className={cur >= prev ? "text-gold" : "text-ivory/60"} data-testid="weekly-trend">
              {cur} this week {cur > prev ? "↑" : cur < prev ? "↓" : "→"} vs {prev} last week
            </span>
          </h2>
        </div>
        <div className="flex gap-6 text-[11px] font-mono uppercase tracking-[0.18em] text-ivory/50">
          <span className="flex items-center gap-2"><span className="h-2 w-2 bg-gold" /> Inquiries</span>
          <span className="flex items-center gap-2"><span className="h-2 w-2 bg-gold/35" /> Deck downloads</span>
        </div>
      </div>
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${WEEKS}, minmax(0, 1fr))` }}>
        {weeks.map((w, i) => {
          const sum = w.inquiries + w.decks;
          return (
            <div key={i} className="flex flex-col items-center gap-3" data-testid={`week-bar-${i}`}>
              <span className="font-mono text-[11px] text-ivory/70 h-4">{sum || ""}</span>
              <div className="relative w-full h-40 flex flex-col-reverse border-b border-gold/20 group">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${(w.inquiries / max) * 100}%` }}
                  transition={{ duration: 0.8, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full bg-gold"
                  title={`${w.inquiries} inquiries`}
                />
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${(w.decks / max) * 100}%` }}
                  transition={{ duration: 0.8, delay: i * 0.05 + 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full bg-gold/35"
                  title={`${w.decks} deck downloads`}
                />
                <div className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap bg-obsidian border border-gold/30 px-2 py-1 text-[10px] font-mono text-ivory opacity-0 group-hover:opacity-100 transition-opacity">
                  {w.inquiries} inq · {w.decks} deck
                </div>
              </div>
              <span className={`font-mono text-[10px] tracking-wider ${i === WEEKS - 1 ? "text-gold" : "text-ivory/40"}`}>{fmt(w.start)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
